import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { formatDate } from '../../utils/dateUtils';

const ACTION_CARDS = [
  {
    key: 'upload',
    title: 'Upload Agreement',
    desc: 'Submit a new account with service agreement and property info documents',
    icon: '📄',
    color: '#3b82f6',
    to: '/sales/accounts/new',
    enabled: true,
  },
  {
    key: 'property_form',
    title: 'Property Info Sheet',
    desc: 'Fill out the digital property information sheet',
    icon: '🏢',
    color: '#8b5cf6',
    to: '/sales/property-info',
    enabled: true,
  },
  {
    key: 'digital_agreement',
    title: 'Service Agreement',
    desc: 'Fill out and sign a digital service agreement',
    icon: '✍️',
    color: '#10b981',
    to: '/sales/service-agreement',
    enabled: true,
  },
  {
    key: 'notices',
    title: 'Generate Notices',
    desc: 'Create towing notices for a property',
    icon: '📋',
    color: '#f59e0b',
    to: '/sales/notices',
    enabled: true,
  },
];

export default function NewAccountHubPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can('manage_tasks');
  const [stats, setStats] = useState({ total: 0, submitted: 0, processing: 0, active: 0 });

  useEffect(() => {
    api.getAccountStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>New Accounts</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/sales/accounts/list')}>
          View All Accounts
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total', value: stats.total, color: '#0f172a' },
          { label: 'Submitted', value: stats.submitted, color: '#3b82f6' },
          { label: 'Processing', value: stats.processing, color: '#f59e0b' },
          { label: 'Active', value: stats.active, color: '#10b981' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: 12, padding: '14px 16px',
            border: '1px solid #e2e8f0', textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Action Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {ACTION_CARDS.map(card => (
          <div
            key={card.key}
            onClick={() => card.enabled && card.to && navigate(card.to)}
            style={{
              background: '#fff',
              borderRadius: 14,
              border: `1px solid ${card.enabled ? '#e2e8f0' : '#f1f5f9'}`,
              padding: 24,
              cursor: card.enabled ? 'pointer' : 'default',
              opacity: card.enabled ? 1 : 0.55,
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
            onMouseEnter={e => { if (card.enabled) { e.currentTarget.style.borderColor = card.color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; } }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = card.enabled ? '#e2e8f0' : '#f1f5f9'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
          >
            {card.badge && (
              <span style={{
                position: 'absolute', top: 12, right: 12,
                background: '#f1f5f9', color: '#94a3b8', fontSize: 10, fontWeight: 700,
                padding: '3px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {card.badge}
              </span>
            )}
            <div style={{ fontSize: 32, marginBottom: 12 }}>{card.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{card.title}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{card.desc}</div>
          </div>
        ))}
      </div>

      {/* Recent submissions */}
      <RecentAccounts canManage={canManage} navigate={navigate} />
    </div>
  );
}

function RecentAccounts({ canManage, navigate }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAccounts({ sort_by: 'created_at', sort_dir: 'desc' })
      .then(data => setAccounts((data || []).slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (accounts.length === 0) return null;

  const STATUS_COLORS = { submitted: '#3b82f6', processing: '#f59e0b', active: '#10b981', rejected: '#dc2626' };

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Recent Submissions</h3>
        <button onClick={() => navigate('/sales/accounts/list')}
          style={{ fontSize: 12, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          View All &rarr;
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Docs</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} onClick={() => navigate(`/sales/accounts/${a.id}`)} style={{ cursor: 'pointer' }}>
                <td style={{ fontWeight: 600 }}>{a.property_name}</td>
                <td>{a.contact_name || '-'}</td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: `${STATUS_COLORS[a.status] || '#94a3b8'}18`, color: STATUS_COLORS[a.status] || '#94a3b8',
                  }}>
                    {a.status?.charAt(0).toUpperCase() + a.status?.slice(1)}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: '#64748b' }}>
                  {formatDate(a.created_at, { month: 'short', day: 'numeric' })}
                </td>
                <td style={{ textAlign: 'center' }}>{a.doc_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
